import { Notification } from '@thiememeulenhoff/types';
import { NotificationService } from '@thiememeulenhoff/ng-api-gateway';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { of, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { withRequestStatus } from '../_utils/request-status.feature';

type NotificationState = {
  notifications: Array<Notification>;
};

const initialState: NotificationState = {
  notifications: [],
};

export const NotificationStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(),
  withMethods((store, notificationService = inject(NotificationService)) => ({
    fetchNotifications: rxMethod<string | undefined>(
      pipe(
        tap(() => patchState(store, { requestStatus: 'loading' })),
        switchMap((methodSlug) =>
          notificationService.fetchNotifications(methodSlug).pipe(
            tapResponse({
              next: (notifications) =>
                patchState(store, { notifications, requestStatus: 'success' }),
              error: (error: Error) =>
                patchState(store, {
                  requestStatus: { error: error.message || 'Failed to fetch' },
                }),
            }),
          ),
        ),
      ),
    ),
    markAsSeen: rxMethod<number>(
      pipe(
        switchMap((notificationId) =>
          notificationService.postNotificationSeen(notificationId).pipe(
            tap(() => {
              patchState(store, (state) => ({
                notifications: state.notifications.map((n) =>
                  n.id === notificationId
                    ? { ...n, seenAt: new Date().toISOString() }
                    : n,
                ),
              }));
            }),
          ),
        ),
      ),
    ),
    deleteNotification: rxMethod<number>(
      pipe(
        switchMap((notificationId) =>
          notificationService.deleteNotification(notificationId).pipe(
            tap(() => {
              patchState(store, (state) => ({
                notifications: state.notifications.filter(
                  (n) => n.id !== notificationId,
                ),
              }));
            }),
          ),
        ),
      ),
    ),
    markMethodNotificationsAsSeen: rxMethod<{
      methodSlug: string | undefined;
      untilNotificationId: number;
    }>(
      pipe(
        switchMap(({ methodSlug, untilNotificationId }) => {
          if (!methodSlug) {
            return of();
          }
          return notificationService
            .postNotificationAllSeen(methodSlug, untilNotificationId)
            .pipe(
              tap(() => {
                const seenAt = new Date().toISOString();
                patchState(store, (state) => ({
                  notifications: state.notifications.map((n) =>
                    n.methodSlug === methodSlug ? { ...n, seenAt } : n,
                  ),
                }));
              }),
            );
        }),
      ),
    ),
  })),
);
